/**
 * BadgeGrid Component
 *
 * Displays a grid of earned badges with a spotlight section for the next badge to earn.
 * Features a progress bar and enhanced visual hierarchy.
 */
import React from 'react';
import { View, Image, StyleSheet, useWindowDimensions } from 'react-native';
import CustomText from './CustomText';
import BadgeCard from './BadgeCard';
import SurpriseBadge from './SurpriseBadge';
import { InlineLoader } from './LoadingOverlay';
import AppStyles from '../AppStyles';
import { getBadgeImage } from '../util/badgeUtils';
import { useTranslation } from '../i18n';

/**
 * Progress Bar Component
 * Shows visual progress toward next badge
 */
const ProgressBar = ({ current, total, color = '#D4A84B', badgeName = null }) => {
  const { t } = useTranslation('badges');
  const progress = Math.min(current / total, 1);
  const targetName = badgeName || t('nextBadgeFallback');

  return (
    <View style={progressStyles.container}>
      <View
        style={progressStyles.track}
        accessibilityRole="progressbar"
        accessibilityValue={{
          min: 0,
          max: total,
          now: current,
        }}
        accessibilityLabel={t('progressTowardLabel', { badge: targetName, current, total })}
      >
        <View
          style={[
            progressStyles.fill,
            { width: `${progress * 100}%`, backgroundColor: color },
          ]}
        />
      </View>
      <CustomText font="medium" size={12} color={AppStyles.colorSet.textSecondary}>
        {t('progressReports', { current, total })}
      </CustomText>
    </View>
  );
};

const progressStyles = StyleSheet.create({
  container: {
    width: '100%',
    alignItems: 'center',
    marginTop: 12,
  },
  track: {
    width: '100%',
    height: 8,
    backgroundColor: '#F0F0F0',
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 6,
  },
  fill: {
    height: '100%',
    borderRadius: 4,
  },
});

/**
 * BadgeGrid Component
 *
 * @param {Object} props
 * @param {Array} props.badges - Array of badge objects with earned status
 * @param {Object} props.nextBadge - Next badge to earn { name, reportsUntilEarned }
 * @param {Object} props.streak - Streak info { current, longest }
 * @param {boolean} props.loading - Whether data is loading
 * @param {number} props.surpriseBadgeCount - Number of surprise badges earned
 */

/**
 * Badge names come from the server, which stores them in English. The list is
 * rendered in-app, so the reader's language is the right one — translate by id
 * and fall back to whatever the server sent for any id not in the catalog.
 */
const badgeText = (t, id, field, fallback) => {
  const key = `badge.${id}.${field}`;
  const translated = id ? t(key) : null;
  return translated && translated !== key ? translated : fallback;
};

const BadgeGrid = ({
 badges = [], nextBadge = null, streak = null, loading = false, surpriseBadgeCount = 0 }) => {
  const { t } = useTranslation('badges');
  const { width } = useWindowDimensions();
  // 3 columns on larger screens (>400px), 2 columns on smaller screens
  const columnsPerRow = width > 400 ? 3 : 2;

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <InlineLoader />
      </View>
    );
  }

  const earnedBadges = badges.filter((b) => b.earned);
  const earnedCount = earnedBadges.length;
  const nextBadgeData = badges.find((b) => !b.earned);

  // Check if streak should be shown (show if >= 1 week)
  const showStreak = streak && streak.current >= 1;

  // Calculate progress for next badge using the badge threshold
  const getProgressInfo = () => {
    if (!nextBadge || !nextBadgeData) return null;
    // Use the badge's threshold as total reports required
    const reportsRequired = nextBadgeData.threshold || (nextBadge.reportsUntilEarned);
    // Current progress = threshold - remaining reports needed
    const currentProgress = reportsRequired - nextBadge.reportsUntilEarned;
    return { current: currentProgress, total: reportsRequired };
  };

  const progressInfo = getProgressInfo();

  // Prepare all badges for display - earned show real info, unearned show as mystery
  const displayBadges = badges.map((badge) => {
    if (badge.earned) {
      return badge;
    }
    // Unearned badges show as generic locked badges
    return {
      ...badge,
      displayName: '???',
      isLocked: true,
    };
  });

  // Add surprise/bonus badge as the 6th item in the grid
  const allBadgesWithBonus = [
    ...displayBadges,
    { id: 'surprise_bonus', isSurpriseBadge: true, count: surpriseBadgeCount },
  ];

  // Split badges into rows based on screen size (3 columns large, 2 columns small)
  const badgeRows = [];
  for (let i = 0; i < allBadgesWithBonus.length; i += columnsPerRow) {
    badgeRows.push(allBadgesWithBonus.slice(i, i + columnsPerRow));
  }

  const allEarned = earnedCount === badges.length && badges.length > 0;

  return (
    <View
      style={styles.container}
      accessibilityRole="region"
      accessibilityLabel={t('badgesSectionLabel', { earned: earnedCount, total: badges.length })}
    >
      {/* Streak indicator - prominent placement at top */}
      {showStreak && (
        <View
          style={styles.streakContainer}
          accessibilityLabel={`${streak.current}-week reporting streak`}
        >
          <View style={styles.streakBadge}>
            <CustomText font="bold" size={18} color="#F97316">
              {'\uD83D\uDD25'}
            </CustomText>
          </View>
          <View style={styles.streakInfo}>
            <CustomText font="semiBold" size={15} color="#F97316">
              {t('weekStreak', { count: streak.current })}
            </CustomText>
            <CustomText font="regular" size={12} color={AppStyles.colorSet.textMuted}>
              {streak.current >= 4
                ? t('onFire')
                : streak.current >= 2
                  ? t('greatConsistency')
                  : t('buildingMomentum')}
            </CustomText>
          </View>
          {streak.longest > streak.current && (
            <View style={styles.bestStreakBadge}>
              <CustomText font="medium" size={10} color={AppStyles.colorSet.textMuted}>
                {t('bestStreak', { count: streak.longest })}
              </CustomText>
            </View>
          )}
        </View>
      )}

      {/* Next Badge Spotlight - only show if there's a badge to earn */}
      {nextBadgeData && nextBadge && (
        <View style={styles.spotlightSection}>
          <View style={styles.spotlightHeader}>
            <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textSecondary}>
              {t('nextToEarn')}
            </CustomText>
            <CustomText font="medium" size={12} color="#996B1F">
              {nextBadge.reportsUntilEarned === 1
                ? t('almostThere')
                : `${nextBadge.reportsUntilEarned} more reports`}
            </CustomText>
          </View>

          <View style={styles.spotlightCard}>
            <View style={styles.spotlightGlow} />
            <Image
              source={getBadgeImage(nextBadgeData.id)}
              style={styles.spotlightImage}
            />
            <CustomText
              font="semiBold"
              size={16}
              color={AppStyles.colorSet.textPrimary}
              style={styles.spotlightName}
            >
              {nextBadgeData.name}
            </CustomText>
            <CustomText
              font="regular"
              size={13}
              color={AppStyles.colorSet.textSecondary}
              style={styles.spotlightSubtitle}
            >
              {badgeText(t, nextBadgeData.id, 'subtitle', nextBadgeData.subtitle)}
            </CustomText>

            {/* Progress Bar */}
            {progressInfo && (
              <ProgressBar
                current={progressInfo.current}
                total={progressInfo.total}
                color="#D4A84B"
                badgeName={nextBadgeData.name}
              />
            )}
          </View>
        </View>
      )}

      {/* All Badges Section - shows earned and locked (mystery) badges */}
      {badges.length > 0 && (
        <View style={styles.earnedSection}>
          <View style={styles.header}>
            <CustomText
              font="semiBold"
              size={14}
              color={AppStyles.colorSet.textSecondary}
            >
              {allEarned ? t('allEarnedTitle') : t('earnedTitle')}
            </CustomText>
            <CustomText
              font="regular"
              size={13}
              color={AppStyles.colorSet.textMuted}
              accessibilityLabel={t('earnedOfTotalLabel', { earned: earnedCount, total: badges.length })}
            >
              {t('earnedOfTotal', { earned: earnedCount, total: badges.length })}
            </CustomText>
          </View>

          <View style={styles.gridWrapper}>
            <View style={styles.grid} accessibilityRole="list">
              {badgeRows.map((row, rowIndex) => (
                <View key={rowIndex} style={styles.row}>
                  {row.map((badge) => (
                    badge.isSurpriseBadge ? (
                      <View key={badge.id} style={styles.surpriseBadgeCell}>
                        <SurpriseBadge count={badge.count} size={72} showLabel compact />
                      </View>
                    ) : (
                      <BadgeCard
                        key={badge.id}
                        id={badge.earned ? badge.id : null}
                        name={
                          badge.isLocked
                            ? badge.displayName
                            : badgeText(t, badge.id, 'name', badge.name)
                        }
                        subtitle={badge.isLocked ? '' : badgeText(t, badge.id, 'subtitle', badge.subtitle)}
                        color={badge.color}
                        earned={badge.earned}
                        earnedAt={badge.earnedAt}
                        compact
                      />
                    )
                  ))}
                  {/* Fill empty cells to maintain grid alignment */}
                  {row.length < columnsPerRow &&
                    Array(columnsPerRow - row.length).fill(null).map((_, i) => (
                      <View key={`empty-${i}`} style={styles.emptyCell} />
                    ))
                  }
                </View>
              ))}
            </View>
          </View>
        </View>
      )}

      {/* All badges earned celebration */}
      {allEarned && (
        <View style={styles.allEarnedContainer}>
          <CustomText font="medium" size={24} style={styles.celebrationEmoji}>
            {'\uD83C\uDF89'}
          </CustomText>
          <CustomText
            font="semiBold"
            size={14}
            color={AppStyles.colorSet.success}
            style={styles.allEarnedText}
          >
            {t('congratulations')}
          </CustomText>
          <CustomText
            font="regular"
            size={13}
            color={AppStyles.colorSet.textSecondary}
            style={styles.allEarnedSubtext}
          >
            {t('allEarnedMessage')}
          </CustomText>
        </View>
      )}

      {/* Empty state - only if no badges data at all */}
      {badges.length === 0 && (
        <View style={styles.emptyState}>
          <CustomText
            font="regular"
            size={14}
            color={AppStyles.colorSet.textMuted}
            style={styles.emptyText}
          >
            {t('startEarning')}
          </CustomText>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 4,
  },
  loadingContainer: {
    paddingVertical: 20,
    alignItems: 'center',
  },

  // Streak Section - prominent top placement
  streakContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 14,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(249, 115, 22, 0.08)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(249, 115, 22, 0.2)',
  },
  streakBadge: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(249, 115, 22, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  streakInfo: {
    flex: 1,
  },
  bestStreakBadge: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 10,
  },

  // Next Badge Spotlight Section
  spotlightSection: {
    marginBottom: 24,
  },
  spotlightHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  spotlightCard: {
    backgroundColor: '#FFFDF7',
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(212, 168, 75, 0.3)',
    shadowColor: '#D4A84B',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 4,
    position: 'relative',
    overflow: 'hidden',
  },
  spotlightGlow: {
    position: 'absolute',
    top: -50,
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(212, 168, 75, 0.1)',
  },
  spotlightImage: {
    width: 100,
    height: 100,
    resizeMode: 'contain',
    marginBottom: 12,
  },
  spotlightName: {
    textAlign: 'center',
    marginBottom: 4,
  },
  spotlightSubtitle: {
    textAlign: 'center',
    maxWidth: 250,
  },

  // Earned Badges Section
  earnedSection: {
    marginTop: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  gridWrapper: {
    alignItems: 'center',
  },
  grid: {
    maxWidth: 450,
    width: '100%',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  emptyCell: {
    flex: 1,
    marginHorizontal: 4,
    minWidth: 80,
  },

  // All Earned Celebration
  allEarnedContainer: {
    marginTop: 16,
    paddingVertical: 20,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(34, 197, 94, 0.08)',
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: 'rgba(34, 197, 94, 0.2)',
  },
  celebrationEmoji: {
    marginBottom: 8,
  },
  allEarnedText: {
    textAlign: 'center',
    marginBottom: 4,
  },
  allEarnedSubtext: {
    textAlign: 'center',
  },

  // Empty State
  emptyState: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  emptyText: {
    textAlign: 'center',
  },

  // Surprise badge cell in grid (matches BadgeCard compact sizing)
  surpriseBadgeCell: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 4,
    marginBottom: 16,
    paddingVertical: 8,
    minWidth: 80,
  },
});

export default BadgeGrid;
